import React, { useState, useCallback } from 'react'
import { ReactComponent as CloseIcon } from '@svgs/keyline/close.svg'
import Drawer from '@components/ui/Drawer'
import Stars from '@components/ui/Stars'
import { SubmitButton } from '@components/ui/Button'
import { SubmitButtonHandlers } from '@components/ui/Button/SubmitButton'
import {
  formatDate,
  getTrustpilotReviews,
  TrustpilotRatings,
  TrustpilotReview
} from '@api/trustpilot'
import trackError from '@helpers/trackError'
import styles from './styles.module.scss'

const PAGE_SIZE = 10

type Props = {
  isOpen: boolean
  onClose: () => void
  ratings: TrustpilotRatings
  initialReviews: TrustpilotReview[]
  productType: string
}

export default function TrustpilotReviewsDrawer({
  isOpen,
  onClose,
  ratings,
  initialReviews,
  productType
}: Props) {
  const [reviews, setReviews] = useState(initialReviews)
  const [page, setPage] = useState(1)
  const [isLoading, setIsLoading] = useState(false)

  const hasMore = reviews.length < ratings.numberOfReviews.total

  const loadMore = useCallback(
    async ({ triggerButtonReady }: SubmitButtonHandlers) => {
      setIsLoading(true)
      try {
        const nextPage = page + 1
        const data = await getTrustpilotReviews(
          productType,
          nextPage,
          PAGE_SIZE
        )
        setReviews(prev => [...prev, ...data.reviews])
        setPage(nextPage)
      } catch (err) {
        trackError(new Error('Trustpilot load more failed'), {
          section: 'PDP Reviews',
          error: err as Error
        })
      } finally {
        setIsLoading(false)
        triggerButtonReady()
      }
    },
    [page, productType]
  )

  return (
    <Drawer
      isDrawerOpen={isOpen}
      onCloseDrawer={onClose}
      size="small"
      origin="right"
    >
      <div className={styles.drawer}>
        <button
          className={styles.drawer__close}
          onClick={onClose}
          aria-label="Close reviews"
        >
          <CloseIcon />
        </button>

        <div className={styles.drawer__header}>
          <span className={styles.drawer__score}>
            {ratings.starsAverage.toFixed(1)}
          </span>
          <span className={styles.drawer__count}>
            Based on {ratings.numberOfReviews.total} reviews
          </span>
        </div>

        <div className={styles.drawer__list}>
          {reviews.map((review, i) => (
            <article key={review.id ?? i} className={styles.reviewTile}>
              <div className={styles.reviewTile__stars}>
                <Stars value={review.stars} color="color2" />
              </div>
              <div className={styles.reviewTile__copy}>
                <p className={styles.reviewTile__body}>{review.content}</p>
              </div>
              <footer className={styles.reviewTile__footer}>
                <span>{review.consumer.displayName}</span>
                <time>{formatDate(review.createdAt)}</time>
              </footer>
            </article>
          ))}

          {hasMore && (
            <SubmitButton
              variant="secondary"
              onClick={loadMore}
              state={isLoading ? 'loading' : 'ready'}
              classes={styles.drawer__loadMore}
            >
              Load more
            </SubmitButton>
          )}
        </div>
      </div>
    </Drawer>
  )
}
